import { useEffect, useMemo, useState } from 'react';

import { useSegmentation } from './SegmentationRenderer';
import { getPreviousSkinAnalysis } from '../../utils/previousSkinAnalysis';
import {
  buildConcernsListFromReport,
  formatAnalysisDate,
  getOverallSkinScore,
} from '../../utils/reportConcernsList';
import {
  buildConcernRadarItems,
  buildPreviousConcernRadarItems,
  buildPreviousRadarModel,
  buildRadarComparisonModel,
  buildRadarModel,
  getPreviousOverallSkinScore,
} from '../../utils/radarChartModels';

import '../../pages/SkinRoutinePage.css';

function RadarChartSvg({ model, isComparisonView }) {
  if (!model) return null;

  return (
    <svg
      className="skin-routine-radar-svg"
      viewBox={`0 0 ${model.size} ${model.size}`}
      role="img"
      aria-label="Skin analysis report chart"
    >
      {model.levels.map((level) => {
        const r = (level / 100) * model.radius;
        return (
          <circle
            key={`level-${level}`}
            cx={model.cx}
            cy={model.cy}
            r={r}
            fill="none"
            stroke="rgba(100, 116, 139, 0.22)"
            strokeWidth="1"
          />
        );
      })}
      {model.axis.map((item, idx) => (
        <line
          key={`axis-${idx}`}
          x1={model.cx}
          y1={model.cy}
          x2={model.cx + Math.cos(item.angle) * model.radius}
          y2={model.cy + Math.sin(item.angle) * model.radius}
          stroke="rgba(100, 116, 139, 0.2)"
          strokeWidth="1"
        />
      ))}
      {isComparisonView ? (
        <>
          <polygon
            points={model.beforePolygon}
            fill="none"
            stroke="rgba(249, 115, 22, 0.95)"
            strokeWidth="1.8"
            strokeDasharray="5 4"
          />
          <polygon
            points={model.afterPolygon}
            fill="none"
            stroke="rgba(37, 99, 235, 0.95)"
            strokeWidth="2.2"
          />
          {model.axis.map((item, idx) => (
            <g key={`compare-point-${idx}`}>
              {item.hasBefore && item.hasAfter ? (
                <line
                  x1={item.beforePointX}
                  y1={item.beforePointY}
                  x2={item.afterPointX}
                  y2={item.afterPointY}
                  stroke={
                    item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.95)'
                      : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.95)'
                        : 'rgba(148, 163, 184, 0.9)'
                  }
                  strokeWidth="2"
                  strokeDasharray={item.changeKind === 'stable' ? '3 3' : 'none'}
                />
              ) : null}
              {item.hasBefore ? (
                <circle cx={item.beforePointX} cy={item.beforePointY} r="2.8" fill="rgba(249, 115, 22, 0.95)" />
              ) : null}
              {item.hasAfter ? (
                <circle
                  cx={item.afterPointX}
                  cy={item.afterPointY}
                  r="3.8"
                  fill={
                    item.changeKind === 'new' ? 'rgba(168, 85, 247, 0.98)'
                      : item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.98)'
                        : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.98)'
                          : 'rgba(37, 99, 235, 0.95)'
                  }
                />
              ) : null}
              <text
                x={item.labelX + (item.labelOffsetX || 0)}
                y={item.labelY}
                textAnchor={item.labelAnchor || 'middle'}
                className={`skin-routine-radar-label skin-routine-radar-label--${item.changeKind}`}
              >
                {item.label}
              </text>
            </g>
          ))}
        </>
      ) : (
        <>
          <polygon
            points={model.polygon}
            fill="none"
            stroke="rgba(37, 99, 235, 0.95)"
            strokeWidth="2"
          />
          {model.axis.map((item, idx) => (
            <g key={`point-${idx}`}>
              <circle cx={item.pointX} cy={item.pointY} r="3.6" fill="rgba(37, 99, 235, 0.95)" />
              <text
                x={item.labelX + (item.labelOffsetX || 0)}
                y={item.labelY}
                textAnchor={item.labelAnchor || 'middle'}
                className="skin-routine-radar-label"
              >
                {item.label}
              </text>
            </g>
          ))}
        </>
      )}
    </svg>
  );
}

const RadarAnalysisSection = ({ report, skinReference, appSessionToken }) => {
  const segmentation = useSegmentation();
  const [previousSkinData, setPreviousSkinData] = useState(null);
  const [radarComparisonTab, setRadarComparisonTab] = useState('after');

  useEffect(() => {
    if (!segmentation.advancedCharts || !skinReference) return undefined;

    if (report?.previous_skin && typeof report.previous_skin === 'object') {
      setPreviousSkinData(report.previous_skin);
      return undefined;
    }

    if (!appSessionToken) {
      setPreviousSkinData(null);
      return undefined;
    }

    let cancelled = false;

    getPreviousSkinAnalysis(skinReference, appSessionToken)
      .then((res) => {
        if (cancelled || !res.success) return;
        setPreviousSkinData(res.previous || null);
      })
      .catch(() => {
        if (!cancelled) setPreviousSkinData(null);
      });

    return () => { cancelled = true; };
  }, [report?.previous_skin, skinReference, appSessionToken, segmentation.advancedCharts]);

  const concernsList = useMemo(
    () => buildConcernsListFromReport(report, {
      showConcerns: segmentation.showConcerns,
      showConditions: segmentation.showConditions,
      allowedNames: segmentation.allowedNames,
    }),
    [report, segmentation],
  );

  const concernRadarItems = useMemo(
    () => (segmentation.advancedCharts ? buildConcernRadarItems(concernsList) : []),
    [concernsList, segmentation.advancedCharts],
  );

  const radarModel = useMemo(
    () => buildRadarModel(concernRadarItems),
    [concernRadarItems],
  );

  const previousConcernRadarItems = useMemo(
    () => (segmentation.advancedCharts ? buildPreviousConcernRadarItems(previousSkinData) : []),
    [previousSkinData, segmentation.advancedCharts],
  );

  const previousRadarModel = useMemo(
    () => buildPreviousRadarModel(previousConcernRadarItems),
    [previousConcernRadarItems],
  );

  const radarComparisonModel = useMemo(
    () => buildRadarComparisonModel(concernRadarItems, previousConcernRadarItems),
    [concernRadarItems, previousConcernRadarItems],
  );

  const overallSkinScore = useMemo(() => getOverallSkinScore(report), [report]);
  const previousOverallSkinScore = useMemo(
    () => getPreviousOverallSkinScore(previousSkinData),
    [previousSkinData],
  );

  const analysisDateText = useMemo(
    () => formatAnalysisDate(report?.ai_report?.created_at),
    [report?.ai_report?.created_at],
  );

  const previousAnalysisDateText = useMemo(
    () => formatAnalysisDate(
      previousSkinData?.created_at
      || previousSkinData?.analysis_date
      || previousSkinData?.createdAt,
    ),
    [previousSkinData],
  );

  const radarDaysBetween = useMemo(() => {
    const current = report?.ai_report?.created_at;
    const previous = previousSkinData?.created_at || previousSkinData?.analysis_date;
    if (!current || !previous) return null;
    const a = new Date(current);
    const b = new Date(previous);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    return Math.round(Math.abs(a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
  }, [report?.ai_report?.created_at, previousSkinData]);

  const hasBeforeAfterRadar = Boolean(radarComparisonModel && previousRadarModel && radarModel);
  const canShowBeforeAfterRadar = Boolean(segmentation.advancedCharts && hasBeforeAfterRadar);
  const isAfterComparisonView = canShowBeforeAfterRadar && radarComparisonTab === 'after';
  const activeRadarModel = canShowBeforeAfterRadar
    ? (radarComparisonTab === 'before' ? previousRadarModel : radarComparisonModel)
    : radarModel;

  const activeRadarScore = canShowBeforeAfterRadar && radarComparisonTab === 'before'
    ? previousOverallSkinScore
    : overallSkinScore;
  const activeRadarDate = canShowBeforeAfterRadar && radarComparisonTab === 'before'
    ? previousAnalysisDateText
    : analysisDateText;

  if (!segmentation.loaded || !segmentation.advancedCharts || !concernsList.length || !activeRadarModel) {
    return null;
  }

  return (
    <div className="skin-routine-section-block skin-routine-radar-analysis-block">
      <div className="skin-routine-section-heading-with-info">
        <h2 className="skin-routine-section-heading">Skin Analysis Report</h2>
      </div>

      {canShowBeforeAfterRadar && (
        <div className="skin-routine-radar-tabs" role="tablist" aria-label="Skin analysis report comparison tabs">
          <button
            type="button"
            role="tab"
            aria-selected={radarComparisonTab === 'before'}
            className={`skin-routine-radar-tab ${radarComparisonTab === 'before' ? 'active' : ''}`}
            onClick={() => setRadarComparisonTab('before')}
          >
            Before
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={radarComparisonTab === 'after'}
            className={`skin-routine-radar-tab ${radarComparisonTab === 'after' ? 'active' : ''}`}
            onClick={() => setRadarComparisonTab('after')}
          >
            After
          </button>
        </div>
      )}

      <div className="skin-routine-radar-panel">
        <div className="skin-routine-radar-panel-header">
          <h3 className="skin-routine-concerns-subheading">
            {canShowBeforeAfterRadar
              ? (radarComparisonTab === 'before' ? 'Before Skin Analysis Report' : 'After Skin Analysis Report')
              : 'Skin Analysis Report'}
          </h3>
        </div>
        <div className="skin-routine-radar-meta">
          {isAfterComparisonView ? (
            <>
              <span className="skin-routine-radar-meta-score">
                Score: {overallSkinScore != null ? `${overallSkinScore}/100` : '--'}
              </span>
              <span className="skin-routine-radar-meta-date">
                From {previousAnalysisDateText || '--'} to {analysisDateText || '--'}
                {radarDaysBetween != null ? ` (${radarDaysBetween} day${radarDaysBetween === 1 ? '' : 's'})` : ''}
              </span>
            </>
          ) : (
            <>
              <span className="skin-routine-radar-meta-score">
                Score: {activeRadarScore != null ? `${activeRadarScore}/100` : '--'}
              </span>
              <span className="skin-routine-radar-meta-date">
                {canShowBeforeAfterRadar && radarComparisonTab === 'before' ? 'Before Date: ' : 'Date: '}
                {activeRadarDate || '--'}
              </span>
            </>
          )}
        </div>

        <RadarChartSvg
          model={activeRadarModel}
          isComparisonView={isAfterComparisonView}
        />

        <div className="skin-routine-radar-legend" aria-label="Radar chart color legend">
          {isAfterComparisonView ? (
            <>
              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before line</span>
              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After line</span>
              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved</span>
              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Needs attention</span>
              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--new" />New in after</span>
              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--stable" />Stable</span>
            </>
          ) : (
            <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />Current scan line</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RadarAnalysisSection;
